import { useCallback, useState } from 'react';
import { getEnv } from 'config';
import { getAppleRedirectURI } from 'config/configure';
import useScript from 'hooks/useScript';
import { APPLE_AUTH_CLIENT_ID, APPLE_SCRIPT_SRC } from 'oauth/constants';
import { AppleAuthErrorCode, DEFAULT_SCOPE } from './constants';
import { UseAppleLoginConfig, UseAppleLoginResult } from './types';
import { isAppleAuthError } from './utils';

export default function useAppleLogin({
  onCancel,
  onClose,
  onError,
  onSuccess,
  scope = DEFAULT_SCOPE,
}: UseAppleLoginConfig): UseAppleLoginResult {
  const scriptStatus = useScript({ src: APPLE_SCRIPT_SRC });
  const [authenticating, setAuthenticating] = useState(false);

  const authenticate = useCallback(async () => {
    if (!window.AppleID) {
      onError?.(new Error('Error loading Apple login script'));
    }

    setAuthenticating(true);

    window.AppleID.auth.init({
      redirectURI: getAppleRedirectURI(),
      scope,
      clientId: getEnv(APPLE_AUTH_CLIENT_ID),
      usePopup: true,
    });

    try {
      const response = await window.AppleID.auth.signIn();
      onSuccess?.(response);
      onClose?.();
    } catch (error) {
      if (error instanceof Error) {
        onError?.(error);
      } else if (isAppleAuthError(error)) {
        if (error.error === AppleAuthErrorCode.POPUP_CLOSED) {
          onCancel?.();
          onClose?.();
        } else {
          onError?.(new Error(error.error));
        }
      } else {
        onError?.(new Error(String(error)));
      }
    } finally {
      setAuthenticating(false);
    }
  }, [onCancel, onClose, onError, onSuccess, scope]);

  return {
    authenticate,
    authenticating,
    scriptStatus,
  };
}
